import { createParser } from 'nuqs';

const SEPARATOR = ',';

export type CreateParseAsEnumListFilterOptions = {
  /** Raw query values that should deserialize to null (no filter). */
  treatAsNull?: readonly string[];
};

/**
 * Creates a nuqs parser for a comma-separated list of enum string values.
 * Null/absent means no filter; invalid segments are dropped.
 */
export function createParseAsEnumListFilter<T extends string>(
  allowedValues: readonly T[],
  options?: CreateParseAsEnumListFilterOptions,
) {
  const allowedSet = new Set<string>(allowedValues);
  const treatAsNull = new Set(options?.treatAsNull ?? []);

  return createParser<T[] | null>({
    parse(value) {
      if (value == null || typeof value !== 'string' || !value.trim()) {
        return null;
      }

      const trimmedValue = value.trim();
      if (treatAsNull.has(trimmedValue)) {
        return null;
      }

      const types = trimmedValue
        .split(SEPARATOR)
        .map((segment) => segment.trim())
        .filter((segment): segment is T => allowedSet.has(segment));

      return types.length > 0 ? types : null;
    },
    serialize(value) {
      if (value == null || value.length === 0) {
        return '';
      }

      return value.join(SEPARATOR);
    },
    eq(a, b) {
      if (a == null && b == null) {
        return true;
      }

      if (a == null || b == null) {
        return false;
      }

      if (a.length !== b.length) {
        return false;
      }

      return a.every((type, index) => type === b[index]);
    },
  });
}
